import { skipToken } from '@reduxjs/toolkit/query';
import { useCallback, useMemo } from 'react';
import { useGetVarietiesForCustomerQuery } from '../impl/blfApi/endpoints/varieties';
import { Variety } from '../impl/blfApi/schemas/variety';

export const useVarietiesByCustomer = (
  customerId: string | undefined,
  cropTypes?: string | string[],
  queryOptions?: {
    skip?: boolean;
  }
): {
  varieties: Variety[] | undefined;
  isVarietiesLoading: boolean;
  getVariety: (varietyId: Variety['id']) => Variety | undefined;
} => {
  const queryParams = useMemo(() => {
    if (!customerId || queryOptions?.skip) {
      return skipToken;
    }

    return { customerId: customerId ?? '', cropTypes };
  }, [customerId, cropTypes, queryOptions?.skip]);

  const { data, isLoading, isFetching } = useGetVarietiesForCustomerQuery(
    queryParams,
    queryOptions
  );

  // Create variety lookup map for performance
  const varietyMap = useMemo<Map<Variety['id'], Variety>>(() => {
    if (!data) return new Map();
    return new Map(data.map((v) => [v.id, v]));
  }, [data]);

  const getVariety = useCallback(
    (varietyId: Variety['id']) => {
      return varietyMap.get(varietyId);
    },
    [varietyMap]
  );

  return {
    varieties: data,
    isVarietiesLoading: isLoading || isFetching,
    getVariety,
  };
};
